import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useInitialData } from './InitialDataContext';

const LANGUAGE_STORAGE_KEY = 'admin_selected_language';
const DEFAULT_LANGUAGE = 'en';

interface LanguageContextType {
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  languages: Array<{ code: string; name: string }>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { state: initialDataState } = useInitialData();

  // Get available languages (excluding special codes like 'zxx')
  const languages = (initialDataState.data?.languages || [])
    .filter((lang) => lang.code !== 'zxx' && lang.code !== 'other')
    .map((lang) => ({ code: lang.code, name: lang.name }));

  // Initialize from localStorage or default
  const [selectedLanguage, setSelectedLanguageState] = useState<string>(() => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored || DEFAULT_LANGUAGE;
  });

  // Validate selected language against available languages
  useEffect(() => {
    if (languages.length > 0) {
      const isValidLanguage = languages.some((lang) => lang.code === selectedLanguage);
      if (!isValidLanguage) {
        // Reset to default if stored language is not available
        const defaultLang =
          languages.find((l) => l.code === DEFAULT_LANGUAGE)?.code || languages[0]?.code;
        if (defaultLang) {
          setSelectedLanguageState(defaultLang);
          localStorage.setItem(LANGUAGE_STORAGE_KEY, defaultLang);
        }
      }
    }
  }, [languages, selectedLanguage]);

  // Persist to localStorage when language changes
  const setSelectedLanguage = (language: string) => {
    setSelectedLanguageState(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  };

  return (
    <LanguageContext.Provider value={{ selectedLanguage, setSelectedLanguage, languages }}>
      {children}
    </LanguageContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
